/**
 * 認証API実サービス（httpOnly Cookie認証方式）
 *
 * @file auth.service.ts
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * バックエンド認証APIとの実際の通信を行うサービス。
 * /api/v1/auth/* エンドポイントと連携。
 * httpOnly Cookie方式を採用し、localStorage使用を完全廃止。
 */

import {
  Language,
  MultiLanguageText,
  Currency,
  LoginRequest,
  RegisterRequest,
  AdminLoginRequest,
  SellerLoginRequest,
  LoginResponse,
  AdminLoginResponse,
  AdminLoginOtpRequiredResponse,
  AdminResendOtpRequest,
  AdminResendOtpResponse,
  SellerLoginResponse,
  User,
  AdminUser,
  UserRole
} from '@/types';
import { safeFetch } from '@shared/utils/api';
import { getCsrfTokenFromCookie, getCsrfToken } from '@shared/utils/csrf';

// APIベースURL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const AUTH_BASE_URL = `${API_BASE_URL}/api/v1/admin/auth`;

// バックエンドレスポンス型定義（backend/app/schemas/に対応）
interface BackendTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

interface BackendUserProfileResponse {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  language: string;
  currency?: string;
  country?: string;
  avatar?: string;
  isActive?: boolean;
  emailVerified?: boolean;
  status?: string;
  forcePasswordChange?: boolean;
  lastLoginAt?: string;
  registrationDate?: string;
  createdAt: string;
  updatedAt: string;
  permissions: string[];
}

// バックエンド→フロントエンド型変換
const convertBackendUserToFrontend = (backendUser: BackendUserProfileResponse): User => {
  return {
    id: backendUser.id,
    email: backendUser.email,
    firstName: backendUser.firstName,
    lastName: backendUser.lastName,
    role: backendUser.role as UserRole,
    language: backendUser.language as Language,
    currency: backendUser.currency as Currency | undefined,
    country: backendUser.country,
    avatar: backendUser.avatar,
    isActive: backendUser.isActive,
    emailVerified: backendUser.emailVerified,
    status: backendUser.status as 'active' | 'suspended' | 'banned' | 'pending_verification' | 'pending_approval' | undefined,
    lastLoginAt: backendUser.lastLoginAt ? new Date(backendUser.lastLoginAt) : undefined,
    registrationDate: backendUser.registrationDate ? new Date(backendUser.registrationDate) : undefined,
    createdAt: new Date(backendUser.createdAt),
    updatedAt: new Date(backendUser.updatedAt)
  };
};

// HTTPヘッダー生成（同期版：GETメソッド用）
function getHeaders(): HeadersInit;
// HTTPヘッダー生成（非同期版：POST/PUT/DELETEメソッド用、CSRF対応）
function getHeaders(withCsrf: boolean, role?: 'buyer' | 'seller' | 'admin'): Promise<HeadersInit>;
function getHeaders(withCsrf = false, role: 'buyer' | 'seller' | 'admin' = 'admin'): HeadersInit | Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // 認証はhttpOnly Cookieで自動送信されるため、Authorizationヘッダーは不要

  // CSRF対応が必要な場合は非同期で処理
  if (withCsrf) {
    return (async () => {
      const csrfToken = getCsrfTokenFromCookie();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      } else {
        // Cookieにない場合、APIから取得（ロール指定）
        const token = await getCsrfToken(role);
        headers['X-CSRF-Token'] = token;
      }
      return headers;
    })();
  }

  return headers;
}


export const authService = {
  /**
   * Buyerログイン（一般購入者）
   * POST /api/v1/auth/login
   */
  loginBuyer: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await safeFetch<BackendTokenResponse>(`${AUTH_BASE_URL}/login`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password
      }),
      errorMessage: 'Login failed. Please check your email and password.'
    });

    // トークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    // ユーザー情報を取得
    const user = await authService.getCurrentUser();

    return {
      user,
      accessToken: response.access_token, // UIでの表示用（実際の認証には使用しない）
      refreshToken: response.refresh_token,
      expiresIn: 3600
    };
  },

  /**
   * 後方互換性のための汎用ログイン（loginBuyerのエイリアス）
   * @deprecated Use loginBuyer() instead
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    return authService.loginBuyer(credentials);
  },

  /**
   * Sellerログイン（販売者）
   * POST /api/v1/seller/auth/login
   */
  loginSeller: async (credentials: SellerLoginRequest): Promise<SellerLoginResponse> => {
    const data = await safeFetch<{
      seller: {
        id: string;
        email: string;
        business_name?: MultiLanguageText;
        business_registration_number?: string;
        is_active: boolean;
        is_verified: boolean;
        status: string;
        created_at: string;
      };
      access_token: string;
      token_type: string;
      role: string;
    }>(`${API_BASE_URL}/api/v1/seller/auth/login`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password
      }),
      errorMessage: 'Seller login failed.'
    });

    // トークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    return {
      seller: {
        id: data.seller.id,
        email: data.seller.email,
        businessName: data.seller.business_name,
        status: data.seller.status,
        commissionRate: 0, // バックエンドレスポンスに存在しないためデフォルト値
        role: UserRole.SELLER
      },
      accessToken: data.access_token,
      refreshToken: '', // バックエンドレスポンスに存在しないため空文字
      expiresIn: 3600
    };
  },

  /**
   * 管理者ログイン（メールOTP 2FA対応）
   * POST /api/v1/admin/auth/login
   *
   * 2FA有効の場合:
   * 1. email + password のみ送信 → {requires_2fa: true, resend_token: "..."}
   * 2. email + password + twofaToken を送信 → アクセストークン発行
   */
  adminLogin: async (credentials: AdminLoginRequest): Promise<AdminLoginResponse | AdminLoginOtpRequiredResponse> => {
    const requestBody: Record<string, string> = {
      email: credentials.email,
      password: credentials.password
    };
    if (credentials.twofaToken) {
      requestBody['twofa_token'] = credentials.twofaToken;
    }

    const data = await safeFetch<
      BackendTokenResponse & { requires_2fa?: boolean; resend_token?: string }
    >(`${API_BASE_URL}/api/v1/admin/auth/login`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(requestBody),
      errorMessage: 'Admin login failed.'
    });

    // 2FA要求フェーズ: OTPをメール送信済み
    if (data.requires_2fa === true) {
      if (!data.resend_token) {
        throw new Error('2FA required but resend_token is missing in response');
      }
      return {
        requiresTwoFa: true,
        resendToken: data.resend_token
      } as AdminLoginOtpRequiredResponse;
    }

    // トークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    // 管理者情報をAPI経由で取得
    const adminUser = await authService.getCurrentAdminUser();

    return {
      user: adminUser,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      requiresTwoFactor: false,
      expiresIn: 3600
    };
  },

  /**
   * OTP再送
   * POST /api/v1/admin/auth/2fa/resend
   */
  resendAdminOtp: async (data: AdminResendOtpRequest): Promise<AdminResendOtpResponse> => {
    const result = await safeFetch<{ sent: boolean; resend_token: string }>(
      `${API_BASE_URL}/api/v1/admin/auth/2fa/resend`,
      {
        method: 'POST',
        headers: await getHeaders(true),
        body: JSON.stringify({
          email: data.email,
          resend_token: data.resendToken
        }),
        errorMessage: 'OTP resend failed.'
      }
    );
    return {
      sent: result.sent,
      resendToken: result.resend_token
    };
  },

  /**
   * Buyer登録（一般購入者）
   * POST /api/v1/auth/register
   */
  registerBuyer: async (data: RegisterRequest): Promise<LoginResponse> => {
    // バックエンドはemailとpasswordのみ受け付ける
    await safeFetch<BackendTokenResponse>(`${AUTH_BASE_URL}/register`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        email: data.email,
        password: data.password
      }),
      errorMessage: 'Buyer registration failed.'
    });

    // 登録後自動ログイン
    return await authService.loginBuyer({
      email: data.email,
      password: data.password
    });
  },

  /**
   * 後方互換性のための汎用登録（registerBuyerのエイリアス）
   * @deprecated Use registerBuyer() instead
   */
  register: async (data: RegisterRequest): Promise<LoginResponse> => {
    return authService.registerBuyer(data);
  },

  /**
   * ログアウト
   * POST /api/v1/auth/logout
   */
  logout: async (): Promise<void> => {
    try {
      await safeFetch<void>(`${AUTH_BASE_URL}/logout`, {
        method: 'POST',
        headers: await getHeaders(true),
        errorMessage: 'Logout process failed.'
      });
    } catch (error) {
      console.warn('ログアウトAPIエラー:', error);
    }
    // httpOnly Cookieはバックエンドで削除されるため、フロントエンドでの削除処理は不要
  },

  /**
   * 現在のユーザー情報取得
   * GET /api/v1/auth/profile
   */
  getCurrentUser: async (signal?: AbortSignal): Promise<User | AdminUser> => {
    const backendUser = await safeFetch<BackendUserProfileResponse>(`${AUTH_BASE_URL}/profile`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: 'Failed to fetch user information.',
      signal,
    });

    return convertBackendUserToFrontend(backendUser);
  },

  /**
   * 現在の管理者情報取得
   * GET /api/v1/admin/auth/profile
   */
  getCurrentAdminUser: async (signal?: AbortSignal): Promise<AdminUser> => {
    const adminData = await safeFetch<BackendUserProfileResponse & { twofa_enabled?: boolean }>(`${API_BASE_URL}/api/v1/admin/auth/profile`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: 'Failed to fetch admin information.',
      signal,
    });

    if (adminData.status === undefined || adminData.status === null) {
      throw new Error('Admin status is required but not found in response.');
    }

    return {
      id: adminData.id,
      email: adminData.email,
      firstName: adminData.firstName,
      lastName: adminData.lastName,
      role: adminData.role as UserRole.ADMIN | UserRole.SUPER_ADMIN | UserRole.EDITOR | UserRole.VIEWER,
      language: 'ja',
      status: adminData.status as 'active' | 'suspended' | 'banned' | 'pending_verification' | 'pending_approval',
      createdAt: new Date(adminData.createdAt),
      updatedAt: new Date(adminData.updatedAt),
      permissions: adminData.permissions,
      twoFactorEnabled: adminData.twofa_enabled !== undefined ? adminData.twofa_enabled : false,
      failedLoginAttempts: 0,
      forcePasswordChange: adminData.forcePasswordChange
    };
  },

  /**
   * 現在の販売者情報取得
   * GET /api/v1/seller/auth/profile
   */
  getCurrentSellerUser: async (signal?: AbortSignal): Promise<User> => {
    const sellerData = await safeFetch<{
      id: string;
      email: string;
      business_name?: { ja?: string; en?: string; ko?: string };
      business_registration_number?: string;
      is_active: boolean;
      is_verified: boolean;
      status: string;
      created_at: string;
    }>(`${API_BASE_URL}/api/v1/seller/auth/profile`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: 'Failed to fetch seller information.',
      signal,
    });

    // Seller情報をUserオブジェクト形式に変換
    return {
      id: sellerData.id,
      email: sellerData.email,
      firstName: (sellerData.business_name?.ja || sellerData.business_name?.en || ''),
      lastName: '',
      role: UserRole.SELLER,
      language: 'ja' as Language,
      isActive: sellerData.is_active,
      status: sellerData.status as 'active' | 'suspended' | 'banned' | 'pending_verification' | 'pending_approval' | undefined,
      createdAt: new Date(sellerData.created_at),
      updatedAt: new Date(sellerData.created_at)
    };
  },

  /**
   * トークンリフレッシュ
   * POST /api/v1/auth/refresh
   */
  refreshToken: async (): Promise<{ accessToken: string; expiresIn: number }> => {
    // httpOnly Cookieのrefresh_tokenはバックエンドで自動的に送信される
    const tokenData = await safeFetch<BackendTokenResponse>(`${AUTH_BASE_URL}/refresh`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({}), // リフレッシュトークンはCookieから自動取得
      errorMessage: 'Token refresh failed. Please log in again.'
    });

    // 新しいトークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    return {
      accessToken: tokenData.access_token, // UIでの表示用
      expiresIn: 3600
    };
  },

  /**
   * パスワードリセット要求
   * POST /api/v1/auth/reset-password
   */
  resetPassword: async (email: string): Promise<void> => {
    await safeFetch<void>(`${AUTH_BASE_URL}/reset-password`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ email }),
      errorMessage: 'Password reset request failed.'
    });
  },

  /**
   * 認証状態確認（新規追加）
   * GET /api/v1/auth/me
   */
  checkAuth: async (): Promise<User | AdminUser> => {
    const backendUser = await safeFetch<BackendUserProfileResponse>(`${AUTH_BASE_URL}/me`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: 'Authentication status check failed.'
    });

    return convertBackendUserToFrontend(backendUser);
  }
};
